import { hash } from 'hash-it';

import type { Endomorphism } from '@code-expert/prelude';
import {
  adt,
  array,
  boolean,
  constant,
  either,
  eq,
  flow,
  fn,
  iots,
  nonEmptyArray,
  not,
  number,
  option,
  ord,
  pipe,
  record,
  set,
  string,
  struct,
  tagged,
} from '@code-expert/prelude';
import type { Blank } from './Blank';
import {
  blankEqById,
  blankEqByIdAndValue,
  BlankId,
  blankIdEq,
  blankPositionEqByStart,
} from './Blank';

export const FREETEXT_MIN_SIZE: iots.Int = iots.brandFromLiteral(1);
export const FREETEXT_MAX_SIZE: iots.Int = iots.brandFromLiteral(40);
export const FREETEXT_DEFAULT_SIZE: iots.Int = iots.brandFromLiteral(20);

const FreetextConfigValueC = iots.struct(
  {
    id: BlankId,
    solution: iots.string,
    size: iots.rangedInteger({ min: FREETEXT_MIN_SIZE, max: FREETEXT_MAX_SIZE }),
  },
  {
    validationRegex: iots.string,
    validationHint: iots.string,
  },
);

export const FreetextConfigC = iots.tagged('freetext', FreetextConfigValueC);

export type FreetextConfig = iots.TypeOf<typeof FreetextConfigC>;
export type FreetextConfigValue = iots.TypeOf<typeof FreetextConfigValueC>;

export const FreetextConfigValueEq: eq.Eq<FreetextConfigValue> = eq.struct<FreetextConfigValue>({
  id: blankIdEq,
  solution: string.Eq,
  size: number.Eq,
  validationRegex: eq.nullable(string.Eq),
  validationHint: eq.nullable(string.Eq),
});

export const ChoiceConfigOptionsC = iots.nonEmptyArray(iots.string);

export type ChoiceConfigOptions = iots.TypeOf<typeof ChoiceConfigOptionsC>;

export const ChoiceConfigValueC = iots.strict({
  id: BlankId,
  solution: iots.string,
  options: ChoiceConfigOptionsC,
  randomOrder: iots.boolean,
});

export const ChoiceConfigC = iots.tagged('choice', ChoiceConfigValueC);

export type ChoiceConfig = iots.TypeOf<typeof ChoiceConfigC>;
export type ChoiceConfigValue = iots.TypeOf<typeof ChoiceConfigValueC>;

export const ChoiceConfigValueEq: eq.Eq<ChoiceConfigValue> = eq.struct({
  id: blankIdEq,
  solution: string.Eq,
  options: array.getEq(string.Eq),
  randomOrder: boolean.Eq,
});

export const NumericConfigValueC = iots.strict({
  id: BlankId,
  solution: iots.string,
  integer: iots.boolean,
});

export const NumericConfigC = iots.tagged('numeric', NumericConfigValueC);

export type NumericConfig = iots.TypeOf<typeof NumericConfigC>;
export type NumericConfigValue = iots.TypeOf<typeof NumericConfigValueC>;

export const NumericConfigSolutionInt = iots.IntFromString;

export const NumericConfigSolutionFloat = iots.NumberFromString;

export const NumericConfigValueEq: eq.Eq<NumericConfigValue> = eq.struct({
  id: blankIdEq,
  solution: string.Eq,
  integer: boolean.Eq,
});

export const BlankConfigC = iots.union([FreetextConfigC, ChoiceConfigC, NumericConfigC]);

export type BlankConfig = iots.TypeOf<typeof BlankConfigC>;

export const blankConfigEq: eq.Eq<BlankConfig> = tagged.getEq({
  freetext: FreetextConfigValueEq,
  choice: ChoiceConfigValueEq,
  numeric: NumericConfigValueEq,
});

export const blankConfigEqById: eq.Eq<BlankConfig> = eq.struct({
  value: eq.struct({
    id: blankIdEq,
  }),
});

export const blankConfigEqByIdAndSolution: eq.Eq<BlankConfig> = eq.struct({
  value: eq.struct({
    id: blankIdEq,
    solution: string.Eq,
  }),
});

export const blankConfigAdt = tagged.build<BlankConfig>();

export const blankConfigKeys = {
  freetext: null,
  choice: null,
  numeric: null,
};

export const BlankConfigTagC = iots.keyof(blankConfigKeys);

export type BlankConfigTag = iots.TypeOf<typeof BlankConfigTagC>;

export const foldBlankConfigTag = adt.foldFromKeys(
  blankConfigKeys satisfies Record<BlankConfig['_tag'], null>,
);

export const getCustomBlankConfigId: (_: BlankConfig) => option.Option<BlankId> = flow(
  option.fromPredicate(({ value: { id, solution } }: BlankConfig) => id !== solution),
  option.map(({ value: { id } }) => id),
);

export const updateBlankConfigById = (config: BlankConfig): Endomorphism<Array<BlankConfig>> =>
  array.map((x) => (blankConfigEqById.equals(x, config) ? config : x));

const updateConfigId = (id: BlankId, solution?: string) =>
  blankConfigAdt.fold({
    choice: (value) =>
      blankConfigAdt.wide.choice(solution != null ? { ...value, id, solution } : { ...value, id }),
    freetext: (value) =>
      blankConfigAdt.freetext(solution != null ? { ...value, id, solution } : { ...value, id }),
    numeric: (value) =>
      blankConfigAdt.numeric(solution != null ? { ...value, id, solution } : { ...value, id }),
  });

export const getEmptyFreetextBlankConfig = (solution: string, id: BlankId): BlankConfig =>
  blankConfigAdt.freetext({ id, solution, size: FREETEXT_DEFAULT_SIZE });

export const getEmptyChoiceBlankConfig = (solution: string, id: BlankId): BlankConfig =>
  blankConfigAdt.choice({ id, solution, options: [solution], randomOrder: true });

export const getEmptyNumericBlankConfig = (solution: string, id: BlankId): BlankConfig =>
  blankConfigAdt.numeric({ id, solution, integer: false });

export const updateConfigsFromBlankChanges =
  (
    prevBlanks: Array<Blank>,
    currentBlanks: Array<Blank>,
    deletedBlank: option.Option<Blank>,
  ): Endomorphism<Array<BlankConfig>> =>
  (blankConfigs) =>
    pipe(
      getSingleChange(prevBlanks, currentBlanks),
      // When exactly one blank changes, we assume it is the same and keep the config synced.
      option.chain(({ id, value }) => {
        const configuredIds = getConfiguredIds(blankConfigs, prevBlanks)(currentBlanks);
        return pipe(
          blankConfigs,
          updateConfigWhere((c) => !configuredIds.has(c.value.id), { id, value }),
          option.fromPredicate(hasSingleConfig(id)),
        );
      }),
      // When a new blank is added, it might be the one just deleted that we keep in deletedBlank.
      // In this case, their start position matches and there is exactly 1 blank more.
      option.orElse(() =>
        updatePreviouslyDeletedBlank(prevBlanks, currentBlanks, deletedBlank, blankConfigs),
      ),
      option.getOrElse(() =>
        pipe(
          blankConfigs,
          addConfigsForNewBlanks(prevBlanks, currentBlanks),
          array.uniq(blankConfigEqById),
        ),
      ),
      cleanupDuplicates(currentBlanks),
    );

const getSingleChange = (
  prevBlanks: Array<Blank>,
  currentBlanks: Array<Blank>,
  expectAdded = false,
) =>
  pipe(
    option.guard(currentBlanks.length === prevBlanks.length + (expectAdded ? 1 : 0)),
    option.chain(() =>
      pipe(
        currentBlanks,
        array.difference(blankEqByIdAndValue)(prevBlanks),
        array.singletonElement,
      ),
    ),
  );

const updatePreviouslyDeletedBlank = (
  prevBlanks: Array<Blank>,
  currentBlanks: Array<Blank>,
  deletedBlank: option.Option<Blank>,
  blankConfigs: Array<BlankConfig>,
) =>
  pipe(
    option.sequenceS({
      deletedBlank,
      newBlank: getSingleChange(prevBlanks, currentBlanks, true),
    }),
    option.filter(({ newBlank, deletedBlank }) =>
      blankPositionEqByStart.equals(deletedBlank.position, newBlank.position),
    ),
    option.chain(({ newBlank: { id, value }, deletedBlank }) =>
      pipe(
        blankConfigs,
        updateConfigWhere((c) => blankEqById.equals(c.value, deletedBlank), { id, value }),
        option.fromPredicate(hasSingleConfig(id)),
      ),
    ),
  );

const updateConfigWhere = (
  where: (config: BlankConfig) => boolean,
  { id, value }: Pick<Blank, 'id' | 'value'>,
) => array.map((c: BlankConfig) => (where(c) ? updateConfigId(id, value)(c) : c));

/**
 * Handles two edge cases regarding duplicates:
 * - Config solution mirrors value of the first blank if values contradict
 *   (e.g., :blank[first]{id=1} … :blank[second]{id=1} => solution is "first")
 * - After resolving duplicates, all values are updated
 *   (e.g., :blank[first]{id=2} … :blank[second]{id=1} => 2: "first", 1: "second")
 */
const cleanupDuplicates = (blanks: Array<Blank>) => (blankConfigs: Array<BlankConfig>) =>
  pipe(blanks, getCurrentBlankData, (data) => pipe(blankConfigs, updateConfigValues(data)));

const getCurrentBlankData = flow(
  nonEmptyArray.groupBy<Blank, BlankId>(struct.get('id')),
  record.map(nonEmptyArray.head),
  record.values,
);

const updateConfigValues = (updates: Array<Pick<Blank, 'id' | 'value'>>) =>
  array.map((c: BlankConfig) =>
    pipe(
      updates,
      array.findFirst(({ id }) => blankIdEq.equals(id, c.value.id)),
      option.match(constant(c), ({ id, value }) => updateConfigId(id, value)(c)),
    ),
  );

export const getConfiguredIds = (blankConfigs: Array<BlankConfig>, prevBlanks: Array<Blank>) =>
  flow(
    array.filter((b: Blank) => pipe(prevBlanks, array.elem(blankEqByIdAndValue)(b))),
    array.map(struct.get('id')),
    array.concat(getUnlinkedConfigIds(blankConfigs, prevBlanks)),
    set.fromArray(blankIdEq),
  );

export const hasSingleConfig = (id: BlankId) =>
  flow(
    array.filter((c: BlankConfig) => blankIdEq.equals(c.value.id, id)),
    array.isSingleton,
  );

const addConfigsForNewBlanks = (prevBlanks: Array<Blank>, currentBlanks: Array<Blank>) =>
  array.concat(
    pipe(
      currentBlanks,
      array.difference<Blank>(blankEqById)(prevBlanks),
      array.map(({ id, value }) => getEmptyFreetextBlankConfig(value, id)),
    ),
  );

export const getUnlinkedConfigIds = (blankConfigs: Array<BlankConfig>, blanks: Array<Blank>) =>
  pipe(
    blankConfigs,
    array.map((c) => c.value.id),
    array.filter((configId) =>
      pipe(
        blanks,
        array.every(({ id }) => !blankIdEq.equals(id, configId)),
      ),
    ),
  );

type ValidateChoiceOptions = (
  value: ChoiceConfig['value'],
) => either.Either<NonEmptyArray<string>, ChoiceConfig['value']>;

const choiceOptionsValidations: Array<ValidateChoiceOptions> = [
  either.fromPredicate(
    ({ options, solution }) => pipe(options, array.some(eq.isEqualTo(string.Eq)(solution))),
    () => ['Include the solution in the choices'],
  ),
  either.fromPredicate(
    ({ options, solution }) => pipe(options, array.some(not(eq.isEqualTo(string.Eq)(solution)))),
    () => ['Add at least two choices'],
  ),
];

const warningsApplicative = either.getApplicativeValidationNEA<string>();

export const validateChoiceOptions: ValidateChoiceOptions = (config) =>
  pipe(
    choiceOptionsValidations,
    array.traverse(warningsApplicative)(fn.apply(config)),
    either.as(config),
  );

type ValidateNumericSolution = (
  value: NumericConfig['value'],
) => either.Either<NonEmptyArray<string>, NumericConfig['value']>;

const numericSolutionValidations: Array<ValidateNumericSolution> = [
  either.fromPredicate(
    ({ integer, solution }) =>
      !integer || pipe(solution, iots.parseOption(NumericConfigSolutionInt), option.isSome),
    () => ['Solution is not an integer'],
  ),
  either.fromPredicate(
    ({ integer, solution }) =>
      integer || pipe(solution, iots.parseOption(NumericConfigSolutionFloat), option.isSome),
    () => ['Solution is not a number'],
  ),
];

export const validateNumericSolution: ValidateNumericSolution = (config) =>
  pipe(
    numericSolutionValidations,
    array.traverse(warningsApplicative)(fn.apply(config)),
    either.as(config),
  );

const ordBySeededHash = (seed: string) =>
  pipe(
    number.Ord,
    ord.contramap((s) => hash(s + seed)),
  );

export const shuffleChoices = (seed: string) => array.sort(ordBySeededHash(seed));
